import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

export interface ImpersonationTarget {
  type: 'patient' | 'agency_admin';
  patientId?: string;
  patientName?: string;
  agencyId?: string;
  agencyName?: string;
  userId?: string;
}

interface ImpersonationSession {
  id: string;
  impersonator_user_id: string;
  impersonated_user_id: string | null;
  impersonated_patient_id: string | null;
  impersonated_agency_id: string | null;
  impersonation_type: string;
  started_at: string;
  ended_at: string | null;
}

export function useImpersonation() {
  const { user } = useAdminAuth();
  const [isLoading, setIsLoading] = useState(false);

  const canImpersonate = useCallback((target: ImpersonationTarget): boolean => {
    if (!user) return false;
    
    // Super admin can impersonate anyone
    if (user.role === 'super_admin') return true;
    
    // Agency admin can only impersonate patients in their agency
    if (user.role === 'agency_admin' && target.type === 'patient') {
      return true; // RLS will enforce agency boundary
    }
    
    return false;
  }, [user]);

  const startImpersonation = useCallback(async (target: ImpersonationTarget): Promise<string | null> => {
    if (!user || !canImpersonate(target)) {
      console.error('Cannot impersonate: insufficient permissions');
      return null;
    }

    setIsLoading(true);
    try {
      // Create impersonation session record
      const { data: session, error } = await supabase
        .from('impersonation_sessions')
        .insert({
          impersonator_user_id: user.id,
          impersonated_user_id: target.userId || null,
          impersonated_patient_id: target.patientId || null,
          impersonated_agency_id: target.agencyId || null,
          impersonation_type: target.type,
          user_agent: navigator.userAgent,
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating impersonation session:', error);
        return null;
      }

      // Log the action
      await supabase.from('admin_audit_logs').insert({
        actor_user_id: user.id,
        action: 'impersonation_started',
        target_resource: target.type,
        target_id: target.patientId || target.agencyId || target.userId,
        metadata: {
          impersonation_session_id: session.id,
          target_name: target.patientName || target.agencyName,
        },
      });

      return session.id;
    } catch (error) {
      console.error('Error starting impersonation:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user, canImpersonate]);

  const logImpersonationAction = useCallback(async (
    sessionId: string,
    actionType: string,
    actionDetails?: Record<string, any>,
    pageUrl?: string
  ) => {
    try {
      await supabase.from('impersonation_actions').insert({
        session_id: sessionId,
        action_type: actionType,
        action_details: actionDetails || null,
        page_url: pageUrl || window.location.pathname,
      });
    } catch (error) {
      console.error('Error logging impersonation action:', error);
    }
  }, []);

  const endImpersonation = useCallback(async (sessionId: string) => {
    try {
      const { error } = await supabase
        .from('impersonation_sessions')
        .update({ ended_at: new Date().toISOString() })
        .eq('id', sessionId);

      if (error) {
        console.error('Error ending impersonation session:', error);
      }

      // Log the end action
      if (user) {
        await supabase.from('admin_audit_logs').insert({
          actor_user_id: user.id,
          action: 'impersonation_ended',
          target_resource: 'impersonation_session',
          target_id: sessionId,
        });
      }
    } catch (error) {
      console.error('Error ending impersonation:', error);
    }
  }, [user]);

  const openImpersonationView = useCallback(async (target: ImpersonationTarget) => {
    const sessionId = await startImpersonation(target);
    if (!sessionId) return;

    // Build the URL for the impersonated view
    let url = '';
    if (target.type === 'patient' && target.patientId) {
      url = `/triage?impersonate=patient&pid=${target.patientId}&sid=${sessionId}`;
    } else if (target.type === 'agency_admin' && target.agencyId) {
      url = `/admin/dashboard?impersonate=agency&aid=${target.agencyId}&sid=${sessionId}`;
    }

    // Open in a new tab for side-by-side view
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [startImpersonation]);

  return {
    isLoading,
    canImpersonate,
    startImpersonation,
    endImpersonation,
    logImpersonationAction,
    openImpersonationView,
  };
}
